import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Form, Modal, Alert, Row, Col } from "react-bootstrap";
import {
  createClass,
  updateClass,
  clearError,
} from "../../redux/slices/classSlice";
import { fetchBoards } from "../../redux/slices/boardSlice";
import { toast } from "react-toastify";
import { FaRegTrashAlt } from "react-icons/fa";

const ClassModal = ({ show, onHide, classData = null }) => {
  const dispatch = useDispatch();
  const isEditMode = !!classData;

  const { isCreating, isUpdating, error, classes = [] } = useSelector((state) => state.classes);
  const { boards = [], isLoading: boardsLoading } = useSelector((state) => state.boards);

  const [formData, setFormData] = useState({
    board_id: "",
    class_name: "",
    image_url: "",
    is_active: true,
  });

  const [imageFile, setImageFile] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (show && boards.length === 0 && !boardsLoading) {
      dispatch(fetchBoards());
    }
  }, [show]);

  useEffect(() => {
    if (show) {
      if (isEditMode && classData) {
        setFormData({
          board_id: classData.board_id || "",
          class_name: classData.class_name || "",
          image_url: classData.image_url || "",
          is_active: classData.is_active !== undefined ? Boolean(classData.is_active) : true,
        });
      } else {
        setFormData({
          board_id: "",
          class_name: "",
          image_url: "",
          is_active: true,
        });
      }
      setImageFile(null);
      setValidationErrors({});
    }
  }, [show, isEditMode, classData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "is_active" ? value === "true" : value,
    }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    if (isEditMode) {
      setFormData((prev) => ({ ...prev, image_url: "" }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.board_id) {
      errors.board_id = "Please select a board";
    }
    if (!formData.class_name.trim()) {
      errors.class_name = "Class name is required";
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const submitData = {
      board_id: formData.board_id,
      class_name: formData.class_name.trim(),
      image_url: formData.image_url,
      is_active: formData.is_active,
    };

    try {
      if (isEditMode) {
        await dispatch(updateClass({ id: classData.id, classData: submitData, imageFile })).unwrap();
      } else {
        await dispatch(createClass({ classData: submitData, imageFile })).unwrap();
      }
      onHide();
    } catch (err) {
      console.error("Class operation failed:", err);
      toast.error(err || "Something went wrong");
    }
  };

  const isSubmitting = isCreating || isUpdating;

  return (
    <Modal show={show} onHide={onHide} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditMode ? "Update Class" : "Create New Class"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => dispatch(clearError())}>
              {error}
            </Alert>
          )}

          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>
                  Select Board <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  name="board_id"
                  value={formData.board_id}
                  onChange={handleInputChange}
                  isInvalid={!!validationErrors.board_id}
                  disabled={isSubmitting}
                >
                  <option value="">Select Board</option>
                  {boards.map((board) => (
                    <option key={board.id} value={board.id}>
                      {board.board_name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {validationErrors.board_id}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>
                  Class Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="class_name"
                  placeholder="Enter class name"
                  value={formData.class_name}
                  onChange={handleInputChange}
                  isInvalid={!!validationErrors.class_name}
                  disabled={isSubmitting}
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors.class_name}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Select
                  name="is_active"
                  value={formData.is_active ? "true" : "false"}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Class Image</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  disabled={isSubmitting}
                />
                {(imageFile || formData.image_url) && (
                  <div className="mt-3 p-2 border rounded d-flex align-items-center gap-3">
                    <img
                      src={imageFile ? URL.createObjectURL(imageFile) : formData.image_url}
                      alt="Preview"
                      style={{
                        width: "50px",
                        height: "50px",
                        objectFit: "cover",
                        borderRadius: "4px",
                      }}
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                    <div className="flex-grow-1 overflow-hidden">
                      <small className="text-muted d-block text-truncate">
                        {imageFile ? imageFile.name : "Current Image"}
                      </small>
                    </div>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={handleRemoveImage}
                      disabled={isSubmitting}
                    >
                      <FaRegTrashAlt />
                    </Button>
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="btn-outline-danger-custom"
            onClick={onHide}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            className="btn-outline-success"
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? "Processing..."
              : isEditMode
              ? "Update Class"
              : "Create Class"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ClassModal;
